import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";

import type {
  CandidateMemberView,
  DispositionView,
  EvidenceSource,
  EvidenceView,
  PatientDisplayableCandidate,
  PosteriorHypothesisView,
  ProfessionalCaseView,
  QuestionOption,
  SemanticInterpretationView,
} from "../contracts/ui";

export function SkipLink() {
  return <a className="skip-link" href="#main-content">Skip to main content</a>;
}

export function ServiceHeader({ professional = false }: { professional?: boolean }) {
  return (
    <header className={professional ? "service-header professional-header" : "service-header"}>
      <div>
        <Link className="service-identity" to={professional ? "/professional/cases" : "/patient/start"}>
          <span className="service-mark" aria-hidden="true"><i /><i /><i /><i /></span>
          <span>UmoCare</span>
        </Link>
        <span className="role-label">{professional ? "Medical professional workspace" : "Clearer health conversations"}</span>
      </div>
    </header>
  );
}

export function LanguageSelector({ value = "en", onChange }: { value?: string; onChange?: (value: string) => void }) {
  return (
    <label className="language-selector">
      <span>Language</span>
      <select value={value} onChange={(event) => onChange?.(event.target.value)}>
        <option value="en">English</option>
        <option value="ny" disabled>Chichewa — translation review pending</option>
      </select>
    </label>
  );
}

export function EmergencyAction({ label = "Get emergency help" }: { label?: string }) {
  return <a className="emergency-action" href="/emergency-shell/index.html">{label}</a>;
}

export function SubjectIndicator({ label }: { label: string }) {
  return <p className="subject-indicator"><strong>About:</strong> {label}</p>;
}

const stages = ["About the person", "What is happening", "Measurements", "Check answers", "What to do next"] as const;

export function StageIndicator({ current }: { current: (typeof stages)[number] }) {
  return (
    <nav className="stage-indicator" aria-label="Assessment stage">
      <ol>
        {stages.map((stage) => (
          <li key={stage} aria-current={stage === current ? "step" : undefined}>{stage}</li>
        ))}
      </ol>
      <p>Safety is checked throughout.</p>
    </nav>
  );
}

export function QuestionHeading({ children }: { children: ReactNode }) {
  return <h1 tabIndex={-1}>{children}</h1>;
}

export function HintText({ children, id }: { children: ReactNode; id?: string }) {
  return <p className="hint-text" id={id}>{children}</p>;
}

export function WhyAskedDisclosure({ children }: { children: ReactNode }) {
  return <details className="why-asked"><summary>Why we ask this</summary><div>{children}</div></details>;
}

interface ChoiceProps {
  legend: string;
  name: string;
  options: QuestionOption[];
  value?: string;
  values?: string[];
  onChange: (value: string | string[]) => void;
  describedBy?: string;
  error?: string;
}

export function RadioAnswerGroup({ legend, name, options, value, onChange, describedBy, error }: ChoiceProps) {
  const errorId = `${name}-error`;
  const description = [describedBy, error ? errorId : undefined].filter(Boolean).join(" ") || undefined;
  return (
    <fieldset className={error ? "answer-group has-error" : "answer-group"} aria-describedby={description}>
      <legend>{legend}</legend>
      {error ? <InlineError id={errorId}>{error}</InlineError> : null}
      {options.map((option) => (
        <label className="choice" key={option.value}>
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span><strong>{option.label}</strong>{option.description ? <small>{option.description}</small> : null}</span>
        </label>
      ))}
    </fieldset>
  );
}

export function CheckboxAnswerGroup({ legend, name, options, values = [], onChange, describedBy, error }: ChoiceProps) {
  const toggle = (optionValue: string) => {
    const next = values.includes(optionValue)
      ? values.filter((item) => item !== optionValue)
      : [...values, optionValue];
    onChange(next);
  };
  const errorId = `${name}-error`;
  return (
    <fieldset className={error ? "answer-group has-error" : "answer-group"} aria-describedby={[describedBy, error ? errorId : undefined].filter(Boolean).join(" ") || undefined}>
      <legend>{legend}</legend>
      {error ? <InlineError id={errorId}>{error}</InlineError> : null}
      {options.map((option) => (
        <label className="choice" key={option.value}>
          <input type="checkbox" name={name} value={option.value} checked={values.includes(option.value)} onChange={() => toggle(option.value)} />
          <span><strong>{option.label}</strong>{option.description ? <small>{option.description}</small> : null}</span>
        </label>
      ))}
    </fieldset>
  );
}

export function UnknownAnswerOption({ name, value, checked, onChange, label = "I do not know" }: {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  label?: string;
}) {
  return <label className="choice unknown-choice"><input type="radio" name={name} value={value} checked={checked} onChange={onChange} /><span>{label}</span></label>;
}

export function TextEntry({ label, value, onChange, hint, error, rows = 6, maxLength = 8000 }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
  rows?: number;
  maxLength?: number;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  return (
    <div className={error ? "field has-error" : "field"}>
      <label htmlFor={id}>{label}</label>
      {hint ? <HintText id={hintId}>{hint}</HintText> : null}
      {error ? <InlineError id={errorId}>{error}</InlineError> : null}
      <textarea id={id} rows={rows} maxLength={maxLength} value={value} onChange={(event) => onChange(event.target.value)} aria-describedby={[hint ? hintId : undefined, error ? errorId : undefined].filter(Boolean).join(" ") || undefined} />
      {value.length > maxLength * 0.9 ? <p className="technical-limit" aria-live="polite">{maxLength - value.length} characters remain. Your current text is preserved.</p> : null}
    </div>
  );
}

export type MicrophoneState = "Ready" | "Listening" | "Stopped" | "Processing" | "Transcription ready" | "Permission denied" | "Microphone unavailable";

export function SpeechCapture({ state, onStart, onStop }: { state: MicrophoneState; onStart: () => void; onStop: () => void }) {
  return (
    <section className="speech-capture" aria-labelledby="speech-heading">
      <h2 id="speech-heading">Speak what is happening</h2>
      <p role="status" aria-live="polite">Microphone: {state}</p>
      {state === "Listening" ? <SecondaryButton onClick={onStop}>Stop recording</SecondaryButton> : <PrimaryButton onClick={onStart}>Start speech input</PrimaryButton>}
      <p>Audio is not sent until you choose to start. A transcription must be checked before it becomes evidence.</p>
    </section>
  );
}

export function TranscriptionReview({ transcription, onChange, onConfirm, onRecordAgain }: {
  transcription: string;
  onChange: (value: string) => void;
  onConfirm: () => void;
  onRecordAgain: () => void;
}) {
  return (
    <section aria-labelledby="transcription-heading">
      <h2 id="transcription-heading">Check the transcription</h2>
      <TextEntry label="Transcribed words" value={transcription} onChange={onChange} />
      <div className="button-stack"><PrimaryButton onClick={onConfirm}>Use this transcription</PrimaryButton><SecondaryButton onClick={onRecordAgain}>Record again</SecondaryButton></div>
    </section>
  );
}

export function UnitSelector({ value, options, onChange }: { value: string; options: QuestionOption[]; onChange: (value: string) => void }) {
  const id = useId();
  return <label className="unit-selector" htmlFor={id}><span>Unit</span><select id={id} value={value} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>;
}

export function ApproximationControl({ checked, onChange }: { checked: boolean; onChange: (value: boolean) => void }) {
  return <label className="choice"><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} /><span>This is approximate</span></label>;
}

export function MeasurementEntry({ label, units, onSubmit, initialValue, initialUnit, initialApproximate = false, initialStatus = "measured" }: { label: string; units: QuestionOption[]; initialValue?: number | null; initialUnit?: string | null; initialApproximate?: boolean; initialStatus?: "measured" | "not_measured" | "unknown"; onSubmit: (value: { value: number | null; unit: string | null; approximate: boolean; status: "measured" | "not_measured" | "unknown" }) => void }) {
  const [value, setValue] = useState(initialValue == null ? "" : String(initialValue));
  const [unit, setUnit] = useState(initialUnit ?? units[0]?.value ?? "");
  const [approximate, setApproximate] = useState(initialApproximate);
  const [status, setStatus] = useState<"measured" | "not_measured" | "unknown">(initialStatus);
  const [error, setError] = useState<string | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const numericValue = Number(value.replace(",", "."));
    if (status === "measured" && (!Number.isFinite(numericValue) || value.trim() === "")) {
      setError("Enter the measurement as a number, or choose an unknown option.");
      return;
    }
    setError(null);
    onSubmit({ value: status === "measured" ? numericValue : null, unit: status === "measured" ? unit : null, approximate, status });
  };
  return (
    <form onSubmit={submit} noValidate>
      {error ? <ErrorSummary heading="There is a problem" errors={[{ href: "#measurement-value", message: error }]} /> : null}
      <fieldset><legend>{label}</legend>
        <label htmlFor="measurement-value">Value</label>
        <div className="measurement-row"><input id="measurement-value" inputMode="decimal" value={value} disabled={status !== "measured"} onChange={(event) => setValue(event.target.value)} aria-invalid={error ? true : undefined} /><UnitSelector value={unit} options={units} onChange={setUnit} /></div>
        <ApproximationControl checked={approximate} onChange={setApproximate} />
        <label className="choice"><input type="radio" name="measurement-status" checked={status === "not_measured"} onChange={() => setStatus("not_measured")} /><span>I have not measured this</span></label>
        <label className="choice"><input type="radio" name="measurement-status" checked={status === "unknown"} onChange={() => setStatus("unknown")} /><span>I do not know the value</span></label>
        <label className="choice"><input type="radio" name="measurement-status" checked={status === "measured"} onChange={() => setStatus("measured")} /><span>I have a measurement</span></label>
      </fieldset>
      <PrimaryButton type="submit">Continue</PrimaryButton>
    </form>
  );
}

export function BackLink({ to = -1, children = "Back" }: { to?: string | number; children?: ReactNode }) {
  return typeof to === "number" ? <button className="back-link" type="button" onClick={() => history.back()}>{children}</button> : <Link className="back-link" to={to}>{children}</Link>;
}

export function PrimaryButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button {...props} className={["button", "button-primary", props.className].filter(Boolean).join(" ")} />;
}

export function SecondaryButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button {...props} className={["button", "button-secondary", props.className].filter(Boolean).join(" ")} />;
}

export function ErrorSummary({ heading, errors }: { heading: string; errors: Array<{ href: string; message: string }> }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    document.title = `Error: ${heading} | UmoCare`;
    ref.current?.focus();
  }, [heading]);
  return <div className="error-summary" role="alert" aria-labelledby="error-summary-heading" tabIndex={-1} ref={ref}><h2 id="error-summary-heading">{heading}</h2><ul>{errors.map((error) => <li key={`${error.href}-${error.message}`}><a href={error.href}>{error.message}</a></li>)}</ul><p>Your other answers have been saved.</p></div>;
}

export function InlineError({ children, id }: { children: ReactNode; id?: string }) {
  return <p className="inline-error" id={id}><span className="visually-hidden">Error: </span>{children}</p>;
}

export function SavingIndicator({ status }: { status: "saved" | "saving" | "not_saved" }) {
  const labels = { saved: "Saved", saving: "Saving", not_saved: "Not saved" } as const;
  return <p className={`saving-indicator ${status}`} role="status" aria-live="polite">{labels[status]}</p>;
}

export function OfflineIndicator({ offline }: { offline: boolean }) {
  return offline ? <section className="connection-alert" role="alert"><h2>Connection lost</h2><p>Your answers on this screen have been kept.</p><p>Emergency contact information remains available.</p></section> : null;
}

const polarityLabels = { present: "Present", absent: "Not present", unknown: "Not sure", uncertain: "Not sure" } as const;

export function InterpretationSummary({ items, onChange }: { items: SemanticInterpretationView[]; onChange?: (id: string) => void }) {
  return <div className="interpretation-list">{items.map((item) => <article key={item.interpretation_id} className="summary-row"><div><h3>{item.patient_label}</h3><dl><div><dt>Status</dt><dd>{polarityLabels[item.polarity]}</dd></div><div><dt>Value</dt><dd>{item.value === null ? "Not measured" : `${String(item.value)}${item.unit ? ` ${item.unit}` : ""}`}</dd></div><div><dt>Source</dt><dd>{item.source_type === "speech" ? "Reported by speech" : "Reported by patient"}</dd></div></dl></div>{onChange ? <SecondaryButton onClick={() => onChange(item.interpretation_id)}>Change</SecondaryButton> : null}</article>)}</div>;
}

export interface CheckAnswerItem { id: string; label: string; value: string; source: string; changeTo: string }

export function CheckAnswersList({ groups }: { groups: Array<{ heading: string; items: CheckAnswerItem[] }> }) {
  return <div>{groups.map((group) => <section key={group.heading} className="check-group"><h2>{group.heading}</h2><dl>{group.items.map((item) => <div className="check-row" key={item.id}><dt>{item.label}</dt><dd>{item.value || "Not answered"}<small>{item.source}</small></dd><dd><Link to={item.changeTo}>Change<span className="visually-hidden"> {item.label}</span></Link></dd></div>)}</dl></section>)}</div>;
}

export function CareDispositionCard({ disposition }: { disposition: DispositionView }) {
  return <section className={`disposition-card ${disposition.action_level}`} aria-labelledby="disposition-heading"><p className="eyebrow">What to do now</p><h1 id="disposition-heading" tabIndex={-1}>{disposition.headline}</h1><p className="action-text">{disposition.action_text}</p><dl><div><dt>How quickly</dt><dd>{disposition.time_window.value}</dd></div><div><dt>Who to contact</dt><dd>{disposition.service.name}</dd></div></dl>{disposition.reasons.length > 0 ? <details><summary>Why this action is shown</summary><ul>{disposition.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul></details> : null}</section>;
}

export function EmergencyCareCard({ serviceName, telephone, actionText, jurisdiction }: { serviceName: string; telephone: string | null; actionText: string; jurisdiction: string }) {
  const resolvedJurisdiction =
    jurisdiction.trim().length > 0 &&
    !/not selected|not configured|unresolved/i.test(jurisdiction);
  const showCountrySpecificTelephone = telephone !== null && resolvedJurisdiction;
  return <section className="emergency-card" role="alert" aria-labelledby="emergency-heading"><h1 id="emergency-heading" tabIndex={-1}>Get emergency help now</h1><p>{actionText}</p><p><strong>Country or territory:</strong> {jurisdiction}</p>{showCountrySpecificTelephone ? <a className="button button-emergency" href={`tel:${telephone}`}>Call {serviceName} now</a> : <><p><strong>Contact your local emergency service now.</strong></p><p>No country or territory has been selected, so we cannot show a contact number.</p><a href="/emergency-shell/index.html">Open emergency contact guidance</a><p>Emergency numbers differ by country and territory. Use the emergency number for where you are now.</p></>}</section>;
}

export function CandidateCategoryList({ candidates }: { candidates: PatientDisplayableCandidate[] }) {
  return <section aria-labelledby="candidate-categories-heading"><h2 id="candidate-categories-heading">Categories for a medical professional to consider</h2><p>These categories organise the information you provided. They are not confirmed diagnoses.</p><p>{candidates.length} candidate {candidates.length === 1 ? "category is" : "categories are"} available.</p>{candidates.length === 0 ? <p>No patient-displayable category was supplied by the clinical server.</p> : <ul className="candidate-categories">{candidates.map((candidate) => <li key={candidate.hypothesis_id}><h3>{candidate.label}</h3><p>{candidate.category}</p><p>{candidate.why_relevant}</p>{candidate.unresolved_information.length > 0 ? <details><summary>Information still needed</summary><ul>{candidate.unresolved_information.map((item) => <li key={item}>{item}</li>)}</ul></details> : null}</li>)}</ul>}</section>;
}

export function SafetyNetList({ instructions }: { instructions: DispositionView["safety_net"] }) {
  return <section aria-labelledby="safety-net-heading"><h2 id="safety-net-heading">What to watch for</h2>{instructions.length === 0 ? <p>No safety-net instruction was supplied. Contact the configured service if you remain concerned.</p> : <ol className="safety-net-list">{instructions.map((item) => <li key={item.instruction_id}><p><strong>If:</strong> {item.change}</p><p><strong>Do:</strong> {item.action}</p><p><strong>When:</strong> {item.time_window}</p><p><strong>Contact:</strong> {item.service}</p><p><strong>If unavailable:</strong> {item.unavailable_action}</p></li>)}</ol>}</section>;
}

export function ShareSummary({ onCopy, downloadHref }: { onCopy: () => void; downloadHref?: string }) {
  return <section aria-labelledby="share-heading"><h2 id="share-heading">Share this summary</h2><p>Preview the summary before sharing. Only patient-visible information is included.</p><div className="button-stack"><PrimaryButton onClick={onCopy}>Copy summary</PrimaryButton><SecondaryButton onClick={() => window.print()}>Print summary</SecondaryButton>{downloadHref ? <a className="button button-secondary" href={downloadHref} download>Download structured summary</a> : null}</div></section>;
}

const sourceLabels: Record<EvidenceSource, string> = {
  patient_reported: "Patient reported",
  proxy_reported: "Proxy reported",
  device_measured: "Device measured",
  professional_observed: "Professional observed",
  record_imported: "Record imported",
  semantically_interpreted: "Semantically interpreted",
  model_derived: "Derived by model",
  unknown: "Unknown",
};

export function EvidenceSourceBadge({ source }: { source: EvidenceSource }) {
  return <span className={`evidence-badge source-${source}`}>{sourceLabels[source]}</span>;
}

export function EvidenceTimeline({ evidence }: { evidence: EvidenceView[] }) {
  return <ol className="evidence-timeline">{evidence.map((item) => <li key={item.evidence_id}><time dateTime={item.time ?? undefined}>{item.time ? new Date(item.time).toLocaleString() : "Time not recorded"}</time><h3>{item.concept}</h3><p>{item.value === null ? (item.recording_state === "not_recorded" ? "Not recorded" : "Unknown") : `${String(item.value)}${item.unit ? ` ${item.unit}` : ""}`}</p><EvidenceSourceBadge source={item.source} /><p>Status: {item.status.replaceAll("_", " ")}</p>{item.flags.length > 0 ? <ul className="evidence-flags">{item.flags.map((flag) => <li key={flag}>{flag}</li>)}</ul> : null}</li>)}</ol>;
}

function formatProbability(probability: number): string { return probability.toFixed(4); }

export function PosteriorTable({ hypotheses, candidates = [] }: { hypotheses: PosteriorHypothesisView[]; candidates?: CandidateMemberView[] }) {
  const candidateMap = new Map(candidates.map((candidate) => [candidate.hypothesis_id, candidate]));
  return <div className="table-scroll" tabIndex={0}><table><caption>Posterior distribution and candidate decision details</caption><thead><tr><th scope="col">Hypothesis</th><th scope="col">Level</th><th scope="col">Posterior</th><th scope="col">Interval</th><th scope="col">Candidate status</th><th scope="col">Threshold</th><th scope="col">Expected omission loss</th><th scope="col">Expected review loss</th></tr></thead><tbody>{hypotheses.map((hypothesis) => { const candidate = candidateMap.get(hypothesis.hypothesis_id); return <tr key={hypothesis.hypothesis_id}><th scope="row">{hypothesis.clinical_label}<code>{hypothesis.hypothesis_id}</code></th><td>{hypothesis.ontological_level}</td><td className="numeric">{formatProbability(hypothesis.posterior_probability)}{hypothesis.exact_zero ? " (definitional zero)" : ""}</td><td className="numeric">{hypothesis.interval ? `${formatProbability(hypothesis.interval[0])}–${formatProbability(hypothesis.interval[1])}` : "Not available"}</td><td>{candidate ? (candidate.included ? "Included" : "Excluded") : "Not evaluated"}</td><td className="numeric">{candidate ? formatProbability(candidate.inclusion_threshold) : "—"}</td><td className="numeric">{candidate ? candidate.expected_omission_loss.toFixed(3) : "—"}</td><td className="numeric">{candidate ? candidate.expected_review_loss.toFixed(3) : "—"}</td></tr>; })}</tbody></table></div>;
}

export function CandidateThresholdMarker({ threshold }: { threshold: number }) {
  return <span className="threshold-marker" style={{ insetInlineStart: `${Math.max(0, Math.min(1, threshold)) * 100}%` }} aria-label={`Candidate inclusion threshold ${formatProbability(threshold)}`} />;
}

export function PosteriorBar({ value, interval, threshold }: { value: number; interval: [number, number] | null; threshold: number }) {
  return <div className="posterior-bar" role="img" aria-label={`Posterior ${formatProbability(value)}; ${interval ? `interval ${formatProbability(interval[0])} to ${formatProbability(interval[1])}; ` : ""}candidate threshold ${formatProbability(threshold)}`}><span className="posterior-fill" style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }} /><CandidateThresholdMarker threshold={threshold} /><span className="posterior-label">{formatProbability(value)}</span></div>;
}

export function ModelAdequacyPanel({ adequacy }: { adequacy: ProfessionalCaseView["model_adequacy"] }) {
  if (adequacy.probability_inadequate === null || adequacy.residual_evidence_probability === null) return <section aria-labelledby="adequacy-heading"><h2 id="adequacy-heading">Model adequacy</h2><p>Not estimated. Calibrated disease probabilities and quantitative model adequacy are unavailable. Professional review is required.</p><ul>{adequacy.numerical_warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul></section>;
  return <section className={adequacy.probability_inadequate >= 0.5 ? "adequacy-panel blocking" : "adequacy-panel"} aria-labelledby="adequacy-heading"><h2 id="adequacy-heading">Model adequacy</h2><dl><div><dt>Probability model inadequate</dt><dd>{formatProbability(adequacy.probability_inadequate)}</dd></div><div><dt>Residual evidence probability</dt><dd>{formatProbability(adequacy.residual_evidence_probability)}</dd></div><div><dt>Approximation</dt><dd>{adequacy.approximation_method}</dd></div><div><dt>Convergence</dt><dd>{adequacy.convergence_status}</dd></div></dl>{adequacy.unsupported_observations.length ? <><h3>Unsupported observations</h3><ul>{adequacy.unsupported_observations.map((item) => <li key={item}>{item}</li>)}</ul></> : null}{adequacy.numerical_warnings.length ? <ul className="warning-list">{adequacy.numerical_warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul> : null}{adequacy.abstention_selected ? <p><strong>The model selected abstention because every forced candidate output had greater expected loss.</strong></p> : null}</section>;
}

export function IndistinguishabilityPanel({ groups }: { groups: ProfessionalCaseView["indistinguishable_groups"] }) {
  return <section aria-labelledby="indistinguishable-heading"><h2 id="indistinguishable-heading">Not distinguishable using the available evidence</h2>{groups.length === 0 ? <p>No indistinguishable group was reported.</p> : groups.map((group) => <article key={group.group_id} className="indistinguishable-group"><h3>{group.hypotheses.join(" and ")}</h3><dl><div><dt>Prior odds</dt><dd>{group.prior_odds}</dd></div><div><dt>Proposed discriminating observation</dt><dd>{group.proposed_observation}</dd></div><div><dt>Expected value</dt><dd>{group.expected_value.toFixed(3)}</dd></div><div><dt>Cost</dt><dd>{group.cost.toFixed(3)}</dd></div></dl><p>Evidence that does not distinguish: {group.non_discriminating_evidence.join(", ")}</p></article>)}</section>;
}

export function NextActionTable({ actions }: { actions: ProfessionalCaseView["next_actions"] }) {
  return <div className="table-scroll" tabIndex={0}><table><caption>Information-acquisition proposals and explicit stopping action</caption><thead><tr><th scope="col">Action</th><th scope="col">Type</th><th scope="col">Gross EVSI</th><th scope="col">Cost</th><th scope="col">Net value</th><th scope="col">Time and feasibility</th></tr></thead><tbody>{actions.map((action) => <tr key={action.action_id}><th scope="row">{action.name}</th><td>{action.action_type === "stop" ? "Stop" : "Information-acquisition proposal — not a clinical order"}</td><td className="numeric">{action.gross_evsi?.toFixed(3) ?? "Not estimated"}</td><td className="numeric">{action.cost?.toFixed(3) ?? "Not estimated"}</td><td className="numeric">{action.net_value?.toFixed(3) ?? "Not estimated"}</td><td>{action.time_sensitivity}; {action.feasibility}{action.safety_constraint ? `; ${action.safety_constraint}` : ""}</td></tr>)}</tbody></table></div>;
}

export function ProvenanceDrawer({ title = "Provenance", provenance }: { title?: string; provenance: Record<string, unknown> }) {
  return <details className="provenance-drawer"><summary>{title}</summary><dl>{Object.entries(provenance).map(([key, value]) => <div key={key}><dt>{key.replaceAll("_", " ")}</dt><dd>{typeof value === "object" ? JSON.stringify(value) : String(value)}</dd></div>)}</dl></details>;
}

export function CaseVersionIndicator({ caseVersion, knowledgeVersion, updatedAt }: { caseVersion: number; knowledgeVersion: string; updatedAt?: string }) {
  return <dl className="version-indicator"><div><dt>Case version</dt><dd>{caseVersion}</dd></div><div><dt>Knowledge version</dt><dd><code>{knowledgeVersion}</code></dd></div>{updatedAt ? <div><dt>Last computed</dt><dd><time dateTime={updatedAt}>{new Date(updatedAt).toLocaleString()}</time></dd></div> : null}</dl>;
}

export function StaleResultBanner({ stale }: { stale: boolean }) {
  return stale ? <section className="stale-banner" role="alert"><h2>Out of date: new evidence has not yet been included</h2><p>This result cannot be released as current patient guidance.</p></section> : null;
}

export function ProfessionalAmendmentDialog({ open, concept, onCancel, onSubmit }: { open: boolean; concept: string; onCancel: () => void; onSubmit: (amendment: { reason: string; source: string; value: string }) => void }) {
  const [reason, setReason] = useState("");
  const [source, setSource] = useState("");
  const [value, setValue] = useState("");
  if (!open) return null;
  return <div className="dialog-backdrop"><div role="dialog" aria-modal="true" aria-labelledby="amendment-heading" className="dialog"><h2 id="amendment-heading">Amend {concept}</h2><p>The original evidence remains in the audit history.</p><label>Amended value<input value={value} onChange={(event) => setValue(event.target.value)} /></label><label>Evidence source<input value={source} onChange={(event) => setSource(event.target.value)} /></label><label>Reason<textarea value={reason} onChange={(event) => setReason(event.target.value)} /></label><div className="button-stack"><PrimaryButton disabled={!reason || !source || !value} title={!reason || !source || !value ? "A value, source, and reason are required." : undefined} onClick={() => onSubmit({ reason, source, value })}>Record amendment</PrimaryButton><SecondaryButton onClick={onCancel}>Cancel</SecondaryButton></div></div></div>;
}

export function BeforeAfterComparison({ previous, updated }: { previous: { posterior: string; candidates: string; disposition: string }; updated: { posterior: string; candidates: string; disposition: string } }) {
  return <div className="before-after"><section><h3>Previous computation</h3><dl><div><dt>Posterior</dt><dd>{previous.posterior}</dd></div><div><dt>Candidate set</dt><dd>{previous.candidates}</dd></div><div><dt>Disposition</dt><dd>{previous.disposition}</dd></div></dl></section><section><h3>Updated computation</h3><dl><div><dt>Posterior</dt><dd>{updated.posterior}</dd></div><div><dt>Candidate set</dt><dd>{updated.candidates}</dd></div><div><dt>Disposition</dt><dd>{updated.disposition}</dd></div></dl></section></div>;
}

export function AuditEventList({ events }: { events: Array<{ eventId: string; timestamp: string; author: string; action: string; reason?: string }> }) {
  return <ol className="audit-events">{events.map((event) => <li key={event.eventId}><time dateTime={event.timestamp}>{new Date(event.timestamp).toLocaleString()}</time><p><strong>{event.action}</strong> by {event.author}</p>{event.reason ? <p>Reason: {event.reason}</p> : null}<code>{event.eventId}</code></li>)}</ol>;
}

export function PatientShell({ children, subjectLabel }: { children: ReactNode; subjectLabel?: string }) {
  return <><SkipLink /><ServiceHeader /><nav aria-label="Language and emergency help" className="patient-utility"><LanguageSelector /><EmergencyAction /></nav>{subjectLabel ? <SubjectIndicator label={subjectLabel} /> : null}<main id="main-content" className="patient-main">{children}</main><footer className="patient-footer"><Link to="/privacy">Privacy</Link><Link to="/accessibility">Accessibility</Link><a href="mailto:help@example.invalid">Help</a></footer></>;
}

export function ProfessionalShell({ children }: { children: ReactNode }) {
  return <><SkipLink /><ServiceHeader professional /><main id="main-content" className="professional-main">{children}</main></>;
}
